// Utility functions for authentication management
import { resetRefreshState } from './refreshManager';
import { clearAllRichTextDrafts } from '@/platform/rich-text/richTextDraftStore';

// Global flags to prevent multiple simultaneous operations
let isClearingAuthState = false;

// Explicitly known auth keys. Anything else auth-related is caught by the
// substring scan in clearAuthStorageOnly().
const AUTH_STORAGE_KEYS = ['token', 'refreshToken', 'rudi_auth_tokens'];
const LOGIN_RETURN_PATH_KEY = 'rudi-login-return-path';

function safeLoginReturnPath(path: string): string | null {
  if (!path.startsWith('/') || path.startsWith('//')) return null;

  try {
    const base = 'https://rudi.local';
    const parsed = new URL(path, base);
    if (parsed.origin !== base || parsed.pathname === '/login') return null;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

/** Save the current internal route so sign-in can resume it. */
export function rememberLoginReturnPath(path?: string): void {
  if (typeof window === 'undefined') return;
  const currentPath =
    path ?? `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const safePath = safeLoginReturnPath(currentPath);
  if (safePath) sessionStorage.setItem(LOGIN_RETURN_PATH_KEY, safePath);
}

/** Read and remove the route saved by the protected application shell. */
export function takeLoginReturnPath(): string | null {
  if (typeof window === 'undefined') return null;
  const storedPath = sessionStorage.getItem(LOGIN_RETURN_PATH_KEY);
  sessionStorage.removeItem(LOGIN_RETURN_PATH_KEY);
  return storedPath ? safeLoginReturnPath(storedPath) : null;
}

/**
 * Drop the browser's auth state synchronously, WITHOUT touching user
 * preferences.
 *
 * Callers that need to navigate away immediately must use this rather than
 * localStorage.clear(): the blanket clear deleted pinned apps, recent apps and
 * every other per-user preference for anyone who hit the login-redirect
 * bailout — which a single backend 502 during a deploy is enough to trigger.
 *
 * Preference keys (`rudi-pinned-apps-*`, `rudi:pinned-apps:v2:*`,
 * `rudi-recent-apps-*`, `rudi-settings`, ...) contain neither "auth" nor
 * "token", and user ids are UUIDs, so the substring scan cannot reach them.
 */
export function clearAuthStorageOnly(): void {
  if (typeof window === 'undefined') return;

  clearAllRichTextDrafts();
  AUTH_STORAGE_KEYS.forEach(key => localStorage.removeItem(key));

  // Clear any other auth-related items
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (key.includes('auth') || key.includes('token'))) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));

  // Reset refresh manager state
  resetRefreshState();
}

/**
 * Clear all authentication state from the browser
 * This includes localStorage, sessionStorage and initiates cookie cleanup
 */
export async function clearAllAuthState() {
  // Guard against multiple simultaneous calls
  if (isClearingAuthState) {
    console.log('[authUtils] Already clearing auth state, skipping duplicate call');
    return;
  }

  isClearingAuthState = true;

  try {
    // Clear localStorage (auth keys only — never user preferences)
    if (typeof window !== 'undefined') {
      clearAuthStorageOnly();

      // Keep the login loop guard and the route that sign-in must resume.
      // Clearing the redirect counter here prevents shouldPreventLoginRedirect()
      // from reaching its three-redirect cap.
      const preservedSessionKeys = [
        'redirecting-to-login',
        'session-expired-redirect',
        'login-redirect-count',
        'last-login-redirect',
        LOGIN_RETURN_PATH_KEY,
      ];
      const preserved = preservedSessionKeys.map(k => [k, sessionStorage.getItem(k)] as const);
      sessionStorage.clear();
      for (const [k, v] of preserved) {
        if (v !== null) sessionStorage.setItem(k, v);
      }

      // Call backend to clear the session cookie (non-blocking). The endpoint
      // is /api/auth/logout — there is no /api/auth/clear (it 404'd on every
      // loop iteration).
      try {
        await fetch('/api/auth/logout', {
          method: 'POST',
          credentials: 'include',
        });
      } catch (error) {
        console.error('Failed to clear server-side auth:', error);
      }
    }
  } finally {
    isClearingAuthState = false;
  }
}

/**
 * Check if we should prevent redirect to login (anti-loop protection)
 * Returns true if redirect should be blocked
 */
export function shouldPreventLoginRedirect(): boolean {
  if (typeof window === 'undefined') return false;

  // Check if already redirecting
  const isRedirecting = sessionStorage.getItem('redirecting-to-login') === 'true';
  if (isRedirecting) {
    console.log('[authUtils] Redirect loop detected - already redirecting to login');
    return true;
  }

  // Check redirect counter (max 3 redirects per session)
  const redirectCount = parseInt(sessionStorage.getItem('login-redirect-count') || '0', 10);
  if (redirectCount >= 3) {
    console.log('[authUtils] Redirect loop detected - max redirect count reached');
    return true;
  }

  // Check timestamp (don't redirect if we just redirected <2s ago)
  const lastRedirectTime = parseInt(sessionStorage.getItem('last-login-redirect') || '0', 10);
  const now = Date.now();
  if (now - lastRedirectTime < 2000) {
    console.log('[authUtils] Redirect loop detected - redirected too recently');
    return true;
  }

  return false;
}

/**
 * Mark that we're redirecting to login
 */
export function markLoginRedirect(): void {
  if (typeof window === 'undefined') return;

  const redirectCount = parseInt(sessionStorage.getItem('login-redirect-count') || '0', 10);

  sessionStorage.setItem('redirecting-to-login', 'true');
  sessionStorage.setItem('login-redirect-count', String(redirectCount + 1));
  sessionStorage.setItem('last-login-redirect', String(Date.now()));
}

/**
 * Reset all redirect tracking (called on successful login/signup)
 */
export function resetLoginRedirectTracking(): void {
  if (typeof window === 'undefined') return;

  sessionStorage.removeItem('redirecting-to-login');
  sessionStorage.removeItem('session-expired-redirect');
  sessionStorage.removeItem('login-redirect-count');
  sessionStorage.removeItem('last-login-redirect');
}
